import { useNavigate } from "react-router-dom"
import { CheckCircle2, ArrowRight } from "lucide-react"
import { Button } from "../../../components/ui/button"
import { Helmet } from "react-helmet-async"

const agronomieCtx = require.context("../../../assets/Agronomie", false, /\.(jpg|jpeg|png|webp)$/i)
const getImg = (s) => (typeof s === "string" ? s : s?.default ?? s)
const ISSAV_HERO = agronomieCtx.keys().length > 0 ? getImg(agronomieCtx(agronomieCtx.keys()[0])) : null

const navItems = [
    { label: "Vision", id: "vision" },
    { label: "BTSP Agriculture", id: "btsp-agriculture" },
    { label: "BTSP Santé animale", id: "btsp-sante-animale" },
    { label: "Responsable", id: "responsable" }
]

export default function Issav() {
    const navigate = useNavigate()
    return (
        <div className="min-h-screen bg-background font-sans text-foreground">
            <Helmet>
                <title>ISSAV - Institut Supérieur des Sciences Agronomiques et Vétérinaires | Université de Ngozi</title>
                <meta
                    name="description"
                    content="ISSAV : formations BTSP en Agriculture et en Santé animale. Formation professionnalisante, stages et orientation vers l'auto-emploi au Burundi."
                />
            </Helmet>
            {/* Creative Masthead */}
            <header className="relative min-h-[60vh] flex items-center bg-primary overflow-hidden">
                <div className="absolute inset-0 z-0">
                    {ISSAV_HERO && <img src={ISSAV_HERO} alt="" className="w-full h-full object-cover opacity-40 mix-blend-luminosity scale-105" aria-hidden />}
                    <div className="absolute inset-0 bg-gradient-to-r from-primary via-primary/80 to-transparent" />
                </div>
                <div className="container relative z-10 py-20">
                    <div className="max-w-3xl">
                        <div className="inline-flex items-center gap-4 mb-8 translate-y-4 animate-in fade-in duration-700 fill-mode-forwards">
                            <span className="text-[10px] uppercase tracking-[0.3em] text-accent font-black">Institut Supérieur</span>
                            <div className="h-px w-12 bg-accent/50" />
                            <span className="text-[10px] text-primary-foreground/60 uppercase tracking-widest font-bold">ISSAV · Vol. 2024</span>
                        </div>
                        <h1 className="heading-1 mb-8 leading-[0.95] font-serif tracking-tight text-white translate-y-4 animate-in fade-in duration-1000 fill-mode-forwards delay-200">
                            <span className="block italic font-light text-accent/90">L&apos;innovation</span>
                            <span className="block text-white mt-2">au service de la terre</span>
                        </h1>
                        <div className="border-l-4 border-accent pl-8 mb-12 max-w-2xl translate-y-4 animate-in fade-in duration-1000 fill-mode-forwards delay-500">
                            <p className="text-lg md:text-xl font-light leading-relaxed text-primary-foreground/90">
                                Former des créateurs d&apos;emplois et non des demandeurs d&apos;emploi, capables de répondre aux défis de
                                <span className="italic block mt-1">l&apos;agriculture et de l&apos;élevage modernes.</span>
                            </p>
                        </div>
                        <div className="flex flex-wrap gap-6 items-center translate-y-4 animate-in fade-in duration-1000 fill-mode-forwards delay-700">
                            <Button
                                onClick={() => navigate("/admission")}
                                className="bg-accent text-accent-foreground px-10 py-7 text-sm font-black transition-all duration-300 border-b-4 border-black/20 rounded-xl"
                            >
                                Candidater Maintenant
                                <ArrowRight className="w-5 h-5 ml-3" />
                            </Button>
                            <Button
                                variant="ghost"
                                onClick={() => document.getElementById("vision")?.scrollIntoView({ behavior: "smooth" })}
                                className="text-white border-2 border-white/20 hover:bg-white/10 hover:border-white px-10 py-7 text-sm font-black rounded-xl backdrop-blur-sm"
                            >
                                Nos Programmes
                            </Button>
                        </div>
                    </div>
                </div>
                <div className="absolute bottom-10 left-1/2 -translate-x-1/2 animate-bounce opacity-20">
                    <div className="w-px h-12 bg-white" />
                </div>
            </header>

            {/* Stats Bar */}
            <div className="border-y border-border bg-secondary/10">
                <div className="container py-4">
                    <div className="flex flex-wrap items-center gap-x-10 gap-y-4">
                        {[
                            { val: "30 ha", label: "Ferme Expérimentale" },
                            { val: "BTSP", label: "Agriculture & Santé animale" },
                            { val: "Durable", label: "Production" }
                        ].map((stat, i) => (
                            <div key={i} className="flex items-center gap-3">
                                <span className="text-xl font-black text-primary">{stat.val}</span>
                                <span className="text-[10px] text-muted-foreground uppercase font-black tracking-widest">{stat.label}</span>
                                {i < 2 && <span className="hidden md:block text-border ml-6 font-light">/</span>}
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Main Article */}
            <article className="container section-padding-sm">
                <div className="grid lg:grid-cols-[250px_1fr] gap-12 lg:gap-20">
                    {/* Sidebar */}
                    <aside className="lg:sticky lg:top-8 lg:self-start hidden lg:block">
                        <nav className="space-y-1">
                            <p className="text-[10px] uppercase tracking-[0.2em] font-black text-muted-foreground mb-6 pb-2 border-b border-border">
                                Sommaire
                            </p>
                            {navItems.map((item, i) => (
                                <a key={item.id} href={`#${item.id}`} className="group flex items-start gap-3 py-2 text-xs font-bold text-muted-foreground hover:text-primary transition-smooth">
                                    <span className="text-accent font-black">0{i + 1}</span>
                                    {item.label}
                                </a>
                            ))}
                        </nav>

                        <div className="mt-12 pt-8 border-t border-border">
                            <div className="bg-primary/5 p-6 border-l-4 border-primary">
                                <h4 className="text-[10px] uppercase tracking-[0.2em] font-black mb-4 text-primary">En Bref</h4>
                                <dl className="space-y-4 text-[11px]">
                                    <div className="flex justify-between items-baseline border-b border-border/40 pb-2">
                                        <dt className="font-bold">Diplômes</dt>
                                        <dd className="text-muted-foreground text-right">BTSP (×2)</dd>
                                    </div>
                                    <div className="flex justify-between items-baseline border-b border-border/40 pb-2">
                                        <dt className="font-bold">Durée</dt>
                                        <dd className="text-muted-foreground">3 ans</dd>
                                    </div>
                                    <div className="flex justify-between items-baseline">
                                        <dt className="font-bold">Pratique</dt>
                                        <dd className="text-muted-foreground">Stage + terrain</dd>
                                    </div>
                                </dl>
                            </div>
                        </div>
                    </aside>

                    {/* Main Content */}
                    <main className="space-y-16">
                        <section id="vision">
                            <h2 className="heading-4 text-foreground mb-6 flex items-center gap-3">
                                <div className="w-1 h-6 bg-accent rounded-full" />
                                Offre de formation — ISSAV
                            </h2>
                            <div className="prose-sm max-w-none space-y-4 text-muted-foreground font-medium leading-relaxed">
                                <p>
                                    L&apos;<strong className="text-foreground">ISSAV</strong> (Institut Supérieur des Sciences Agronomiques et Vétérinaires) propose des programmes visant à former des{" "}
                                    <strong className="text-foreground">créateurs d&apos;emplois</strong> et non des demandeurs d&apos;emploi, capables de :
                                </p>
                                <ul className="space-y-2 list-disc list-inside">
                                    <li>S&apos;installer en tant qu&apos;indépendants ;</li>
                                    <li>Accompagner les agriculteurs et éleveurs ;</li>
                                    <li>Contribuer au développement rural durable ;</li>
                                    <li>Participer à la réduction de la pauvreté.</li>
                                </ul>
                                <p className="text-foreground font-bold pt-2">Points forts des formations</p>
                                <div className="grid sm:grid-cols-1 gap-3 not-prose">
                                    {[
                                        "Formation professionnalisante (théorie + pratique)",
                                        "Stage obligatoire + travail de terrain",
                                        "Forte orientation vers l&apos;auto-emploi",
                                        "Adaptation aux réalités rurales du Burundi",
                                        "Développement de compétences entrepreneuriales"
                                    ].map((line, i) => (
                                        <div key={i} className="flex items-start gap-3 p-3 bg-secondary/5 border border-border/50 rounded-sm">
                                            <CheckCircle2 className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                                            <span className="text-[11px] font-bold leading-snug">{line}</span>
                                        </div>
                                    ))}
                                </div>
                                <p className="pt-4">
                                    L&apos;ISSAV offre une formation complète en sciences agronomiques et vétérinaires avec un accent sur la pratique. Notre ferme expérimentale de{" "}
                                    <strong className="text-foreground">30 hectares</strong> permet aux étudiants d&apos;apprendre en faisant.
                                </p>
                            </div>
                        </section>

                        <section id="formations-intro" className="scroll-mt-0">
                            <h2 className="heading-4 text-foreground mb-6 flex items-center gap-3">
                                <div className="w-1 h-6 bg-accent rounded-full" />
                                Noms officiels des formations
                            </h2>
                            <div className="prose-sm max-w-none text-muted-foreground font-medium leading-relaxed">
                                <p>L&apos;ISSAV propose deux formations principales sanctionnées par le diplôme de :</p>
                                <ol className="list-decimal list-inside space-y-2 mt-3">
                                    <li>
                                        <strong className="text-foreground">Brevet de Technicien Supérieur Professionnel (BTSP)</strong> en Agriculture ;
                                    </li>
                                    <li>
                                        <strong className="text-foreground">Brevet de Technicien Supérieur Professionnel (BTSP)</strong> en Santé animale.
                                    </li>
                                </ol>
                            </div>
                        </section>

                        <section id="btsp-agriculture">
                            <h2 className="heading-4 text-foreground mb-6 flex items-center gap-3">
                                <div className="w-1 h-6 bg-accent rounded-full" />
                                BTSP en Agriculture
                            </h2>
                            <div className="prose-sm max-w-none space-y-6 text-muted-foreground font-medium leading-relaxed">
                                <div>
                                    <h3 className="text-foreground text-base font-bold mb-3">A. Objectif général</h3>
                                    <p className="mb-2">Former des techniciens supérieurs capables de :</p>
                                    <ul className="space-y-2 list-disc list-inside">
                                        <li>Maîtriser les techniques modernes de production agricole (vivrière, maraîchère et de rente) ;</li>
                                        <li>Diagnostiquer les problèmes agricoles et proposer des solutions adaptées ;</li>
                                        <li>Accompagner les agriculteurs dans l&apos;amélioration de leur productivité ;</li>
                                        <li>Promouvoir une agriculture durable et rentable.</li>
                                    </ul>
                                </div>
                                <div>
                                    <h3 className="text-foreground text-base font-bold mb-3">B. Prérequis d&apos;admission</h3>
                                    <p className="font-semibold text-foreground/90">B.1. Niveau requis :</p>
                                    <ul className="space-y-2 list-disc list-inside mt-2">
                                        <li>Diplôme d&apos;État (Humanités générales, pédagogiques ou techniques) ;</li>
                                        <li>Diplôme A2 agricole (ITAB ou équivalent).</li>
                                    </ul>
                                    <p className="font-semibold text-foreground/90 mt-4">B.2. Conditions spécifiques :</p>
                                    <ul className="space-y-2 list-disc list-inside mt-2">
                                        <li>Avoir passé l&apos;Examen d&apos;État (réussi ou non) ;</li>
                                        <li>Pour les étrangers, avoir l&apos;équivalence des documents ou diplômes exigés.</li>
                                    </ul>
                                </div>
                                <div>
                                    <h3 className="text-foreground text-base font-bold mb-3">C. Débouchés professionnels</h3>
                                    <p className="mb-2">Les diplômés peuvent exercer comme :</p>
                                    <ul className="space-y-2 list-disc list-inside">
                                        <li>Conseiller agricole indépendant ;</li>
                                        <li>Technicien en production végétale ;</li>
                                        <li>Agent de vulgarisation agricole ;</li>
                                        <li>Gestionnaire d&apos;exploitation agricole ;</li>
                                        <li>Entrepreneur agroalimentaire.</li>
                                    </ul>
                                    <p className="font-semibold text-foreground/90 mt-4">C.1. Secteurs d&apos;insertion</p>
                                    <ul className="space-y-2 list-disc list-inside mt-2">
                                        <li>Agriculture vivrière et commerciale ;</li>
                                        <li>ONG et projets de développement rural ;</li>
                                        <li>Coopératives agricoles ;</li>
                                        <li>Institutions publiques agricoles ;</li>
                                        <li>Entreprises agroalimentaires ;</li>
                                        <li>Auto-emploi (consultance, exploitation agricole).</li>
                                    </ul>
                                </div>
                            </div>
                        </section>

                        <section id="btsp-sante-animale">
                            <h2 className="heading-4 text-foreground mb-6 flex items-center gap-3">
                                <div className="w-1 h-6 bg-accent rounded-full" />
                                BTSP en Santé animale
                            </h2>
                            <div className="prose-sm max-w-none space-y-6 text-muted-foreground font-medium leading-relaxed">
                                <div>
                                    <h3 className="text-foreground text-base font-bold mb-3">A. Objectif général</h3>
                                    <p className="mb-2">Former des techniciens capables de :</p>
                                    <ul className="space-y-2 list-disc list-inside">
                                        <li>Assurer les soins de base aux animaux ;</li>
                                        <li>Diagnostiquer les maladies animales courantes ;</li>
                                        <li>Participer à la prévention et au contrôle des maladies ;</li>
                                        <li>Améliorer la productivité des élevages.</li>
                                    </ul>
                                </div>
                                <div>
                                    <h3 className="text-foreground text-base font-bold mb-3">B. Prérequis d&apos;admission</h3>
                                    <p className="font-semibold text-foreground/90">B.1. Niveau requis :</p>
                                    <ul className="space-y-2 list-disc list-inside mt-2">
                                        <li>Diplôme d&apos;État (Humanités générales, pédagogiques ou techniques) ;</li>
                                        <li>Diplôme A2 agricole (ITAB ou équivalent).</li>
                                    </ul>
                                    <p className="font-semibold text-foreground/90 mt-4">B.2. Conditions spécifiques :</p>
                                    <ul className="space-y-2 list-disc list-inside mt-2">
                                        <li>Avoir passé l&apos;Examen d&apos;État (réussi ou non) ;</li>
                                        <li>Pour les étrangers, avoir l&apos;équivalence des documents ou diplômes exigés.</li>
                                    </ul>
                                </div>
                                <div>
                                    <h3 className="text-foreground text-base font-bold mb-3">C. Débouchés professionnels</h3>
                                    <p className="mb-2">Les diplômés peuvent devenir :</p>
                                    <ul className="space-y-2 list-disc list-inside">
                                        <li>Assistant vétérinaire ;</li>
                                        <li>Technicien en santé animale ;</li>
                                        <li>Agent d&apos;élevage ;</li>
                                        <li>Conseiller en production animale ;</li>
                                        <li>Prestataire de services vétérinaires.</li>
                                    </ul>
                                    <p className="font-semibold text-foreground/90 mt-4">C.1. Secteurs d&apos;insertion</p>
                                    <ul className="space-y-2 list-disc list-inside mt-2">
                                        <li>Cliniques et cabinets vétérinaires ;</li>
                                        <li>Fermes d&apos;élevage (bovins, caprins, volailles…) ;</li>
                                        <li>ONG et projets d&apos;élevage ;</li>
                                        <li>Services publics vétérinaires ;</li>
                                        <li>Industrie agro-pastorale ;</li>
                                        <li>Auto-emploi (services vétérinaires privés).</li>
                                    </ul>
                                </div>
                            </div>
                        </section>

                        <section id="responsable">
                            <h2 className="heading-4 text-foreground mb-6 flex items-center gap-3">
                                <div className="w-1 h-6 bg-accent rounded-full" />
                                Responsable de la formation
                            </h2>
                            <div className="p-6 rounded-lg border border-border bg-secondary/5 max-w-xl">
                                <p className="text-[11px] uppercase tracking-wider font-black text-muted-foreground mb-2">Nom complet du responsable</p>
                                <p className="text-foreground font-bold text-lg">NSHIMIRIMANA Dieudonné</p>
                                <p className="text-[11px] uppercase tracking-wider font-black text-muted-foreground mt-4 mb-1">Titre / fonction</p>
                                <p className="text-sm text-muted-foreground">Directeur de l&apos;ISSAV</p>
                                <p className="text-sm text-muted-foreground mt-4 italic border-t border-border/60 pt-4">
                                    Photo officielle du responsable : en attente car les photos ont été prises durant les jours de mon voyage en Italie.
                                </p>
                            </div>
                        </section>
                    </main>
                </div>
            </article>

            {/* CTA Footer */}
            <footer className="bg-primary text-white border-t-8 border-accent">
                <div className="container py-16 text-center max-w-2xl">
                    <span className="text-[10px] uppercase tracking-[0.3em] text-accent font-black mb-4 block">Prochaine Étape</span>
                    <h2 className="heading-2 mb-8 leading-tight">
                        Prêt à innover<br />
                        dans l&apos;agriculture ?
                    </h2>
                    <Button
                        onClick={() => navigate("/admission")}
                        className="bg-accent text-accent-foreground px-10 py-4 text-base font-black hover:bg-accent/90 transition-smooth group rounded-md shadow-2xl"
                    >
                        Postuler Maintenant
                        <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                    </Button>
                </div>
            </footer>
        </div>
    )
}
